import React, { useState } from 'react';
import { axiosInstanceV2 } from '../../services/axios';
import { toast } from 'react-toastify';
import {
	testDowngradeSubscription,
	testDowngradeToFree,
	testExpireNonRecurring,
	testFinalizeCancellation,
	testUsageAlert,
} from '../../services/subscription';
import useSubscription from '../../hooks/useSubscription';
import './developer.scss';

/**
 * Developer tools — subscription test harness.
 *
 * These endpoints skip the waiting, not the logic: each one runs the same
 * handler the real scheduled job would, so a pass here means the production
 * path works. That only holds if the RIGHT action is run against the RIGHT
 * state, which is why every card states its preconditions and this page checks
 * them against the live subscription before enabling the button.
 *
 * Two states are easy to confuse and behave completely differently:
 *   - scheduled CANCELLATION  (cancel_at_cycle_end) -> ends up on Free
 *   - scheduled DOWNGRADE     (downgrade_to_plan_id) -> ends up on a cheaper paid plan
 * Running the cancellation action against a scheduled downgrade aborts with
 * "no subscription with cancel_at_cycle_end found", which reads like a bug and
 * is not one.
 */

const FONT = { base: '15px', small: '14px' };

function formatDate(value) {
	if (!value) return '—';
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
}

/** A precondition, evaluated live so a tester cannot run the wrong action. */
function Requirement({ met, children }) {
	return (
		<li
			className="d-flex align-items-start gap-2 mb-1"
			style={{ fontSize: FONT.small, color: met ? '#3F8A17' : '#B42318' }}
		>
			<span style={{ fontWeight: 700, lineHeight: 1.4 }}>{met ? '✓' : '✗'}</span>
			<span>{children}</span>
		</li>
	);
}

function TestAction({
	title,
	description,
	whenItWorks,
	requirements = [],
	steps = [],
	outcome,
	buttonLabel,
	isLoading,
	onRun,
	tone = 'danger',
	// Optional second action for cards where one variant is worth running
	// separately — e.g. raising a single alert vs. raising duplicates of it.
	// Omitted on every other card, so they render exactly as before.
	secondaryLabel,
	isSecondaryLoading,
	onRunSecondary,
}) {
	const blocked = requirements.some((r) => !r.met);

	return (
		<article className="border rounded-3 p-4 h-100 d-flex flex-column bg-white">
			<h4
				className="mb-2"
				style={{ fontSize: '17px', fontWeight: 700 }}
			>
				{title}
			</h4>

			<p
				className="mb-3"
				style={{ fontSize: FONT.base, color: '#334155' }}
			>
				{description}
			</p>

			{whenItWorks && (
				<div
					className="mb-3 p-3 rounded"
					style={{ backgroundColor: '#F1F5F9', fontSize: FONT.small, color: '#334155' }}
				>
					<strong>When to use this:</strong> {whenItWorks}
				</div>
			)}

			{requirements.length > 0 && (
				<div className="mb-3">
					<div
						className="mb-2"
						style={{ fontSize: FONT.small, fontWeight: 700 }}
					>
						Preconditions (checked live)
					</div>
					<ul className="list-unstyled mb-0">
						{requirements.map((r) => (
							<Requirement
								key={r.label}
								met={r.met}
							>
								{r.label}
							</Requirement>
						))}
					</ul>
				</div>
			)}

			{steps.length > 0 && (
				<div className="mb-3">
					<div
						className="mb-2"
						style={{ fontSize: FONT.small, fontWeight: 700 }}
					>
						Do this first
					</div>
					<ol
						className="mb-0 ps-3"
						style={{ fontSize: FONT.small, color: '#475569' }}
					>
						{steps.map((step) => (
							<li
								key={step}
								className="mb-1"
							>
								{step}
							</li>
						))}
					</ol>
				</div>
			)}

			{outcome && (
				<div
					className="mb-3 p-3 rounded"
					style={{ backgroundColor: '#FFF7ED', fontSize: FONT.small, color: '#7C2D12' }}
				>
					<strong>What this will do:</strong> {outcome}
				</div>
			)}

			<div className="mt-auto">
				<button
					type="button"
					className={`btn btn-${tone} w-100`}
					style={{ fontSize: FONT.base, padding: '10px' }}
					disabled={isLoading || blocked}
					onClick={onRun}
					title={blocked ? 'Preconditions above are not met' : undefined}
				>
					{isLoading ? 'Running…' : buttonLabel}
				</button>
				{secondaryLabel && onRunSecondary && (
					<button
						type="button"
						className="btn btn-outline-secondary w-100 mt-2"
						style={{ fontSize: FONT.base, padding: '10px' }}
						disabled={isSecondaryLoading || blocked}
						onClick={onRunSecondary}
						title={blocked ? 'Preconditions above are not met' : undefined}
					>
						{isSecondaryLoading ? 'Running…' : secondaryLabel}
					</button>
				)}
				{blocked && (
					<p
						className="mb-0 mt-2 text-center"
						style={{ fontSize: '13px', color: '#B42318' }}
					>
						Cannot run — preconditions above are not met.
					</p>
				)}
			</div>
		</article>
	);
}

/**
 * Grouped by the DOMAIN each tool exercises, not by the queue it happens to use.
 * A tester looking for "why didn't the limit alert appear" goes to
 * Notifications; someone chasing a downgrade goes to Subscription. Adding a new
 * area means adding an entry here and a matching block below — the tab bar is
 * generated from this list, so the two cannot drift apart.
 */
const TABS = [
	{ key: 'subscription', label: 'Subscription' },
	{ key: 'notifications', label: 'Notifications' },
	{ key: 'diagnostics', label: 'Diagnostics' },
];

function Developer() {
	const {
		subscription,
		isSubscriptionRecurring,
		subscriptionScheduledForCancellation,
		subscriptionPaymentMethod,
	} = useSubscription();

	const [activeTab, setActiveTab] = useState('subscription');

	const [loading, setLoading] = useState({
		sync_plan: false,
		process_downgrade: false,
		downgrade_to_free: false,
		finalize_cancellation: false,
	});

	// Live state drives every precondition below.
	const isDowngradeScheduled = Boolean(subscription?.is_downgrade_scheduled);
	const isFree = subscription?.plan?.key === 'free';
	const isPaid = Boolean(subscription?.plan) && !isFree;
	const canRepriceInPlace = subscriptionPaymentMethod === 'upi';

	const handleSyncPlans = async () => {
		try {
			setLoading((prev) => ({ ...prev, sync_plan: true }));
			await axiosInstanceV2.get('/razorpay/sync-plans');
			toast.success('Plans synced successfully.');
		} catch (error) {
			toast.error(error?.response?.data?.message ?? 'Unable to sync plans.');
		} finally {
			setLoading((prev) => ({ ...prev, sync_plan: false }));
		}
	};

	const runTest = async (loadingKey, request, successMessage) => {
		try {
			setLoading((prev) => ({ ...prev, [loadingKey]: true }));
			const result = await request();
			toast.success(result?.message ?? successMessage);
			window.setTimeout(() => window.location.reload(), 1200);
		} catch (error) {
			toast.error(error?.response?.data?.message ?? 'Test could not be completed.');
		} finally {
			setLoading((prev) => ({ ...prev, [loadingKey]: false }));
		}
	};

	return (
		<main
			className="container py-4"
			style={{ fontSize: FONT.base }}
		>
			<header className="mb-4">
				<div className="d-flex align-items-center gap-2 mb-2">
					<h2
						className="mb-0"
						style={{ fontSize: '24px', fontWeight: 700 }}
					>
						Developer test tools
					</h2>
					<span className="badge text-bg-secondary">Internal</span>
				</div>
				<p
					className="mb-0"
					style={{ color: '#475569' }}
				>
					These run the same handlers the real scheduled jobs use, just without the wait — so a pass
					here means the production path works. Each card lists what must be true before it can run,
					and the button stays disabled until it is.
				</p>
			</header>

			{/* ── domain tabs ──────────────────────────────────────────────── */}
			{/* role=tablist so screen readers announce this as a tab set rather
			    than three unrelated buttons. */}
			<div
				className="developer-tabs mb-4"
				role="tablist"
				aria-label="Developer tool categories"
			>
				{TABS.map((tab) => {
					const isActive = activeTab === tab.key;
					return (
						<button
							key={tab.key}
							type="button"
							role="tab"
							aria-selected={isActive}
							className={`developer-tabs__tab ${isActive ? 'developer-tabs__tab--active' : ''}`}
							onClick={() => setActiveTab(tab.key)}
						>
							{tab.label}
						</button>
					);
				})}
			</div>

			{activeTab === 'subscription' && (
				<>
					{/* ── live state ───────────────────────────────────────────────── */}
					<section
						className="mb-4 p-4 rounded"
						style={{ backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0' }}
					>
						<h3
							className="mb-3"
							style={{ fontSize: '18px', fontWeight: 700 }}
						>
							This organisation right now
						</h3>
						<div className="row g-3">
							<div className="col-6 col-lg-3">
								<div style={{ fontSize: '13px', color: '#64748B' }}>Current plan</div>
								<div style={{ fontWeight: 600 }}>{subscription?.plan?.name ?? '—'}</div>
							</div>
							<div className="col-6 col-lg-3">
								<div style={{ fontSize: '13px', color: '#64748B' }}>Status</div>
								<div style={{ fontWeight: 600 }}>
									{subscription?.status_display?.label ?? subscription?.status ?? '—'}
								</div>
							</div>
							<div className="col-6 col-lg-3">
								<div style={{ fontSize: '13px', color: '#64748B' }}>Billing model</div>
								<div style={{ fontWeight: 600 }}>
									{isSubscriptionRecurring ? 'Recurring' : 'One-time'}
									{subscriptionPaymentMethod ? ` · ${subscriptionPaymentMethod}` : ''}
								</div>
							</div>
							<div className="col-6 col-lg-3">
								<div style={{ fontSize: '13px', color: '#64748B' }}>
									{isSubscriptionRecurring ? 'Renews / ends' : 'Expires'}
								</div>
								<div style={{ fontWeight: 600 }}>
									{formatDate(subscription?.current_end ?? subscription?.next_billing_date)}
								</div>
							</div>
						</div>

						<hr className="my-3" />

						<div className="d-flex flex-wrap gap-4">
							<div>
								<div style={{ fontSize: '13px', color: '#64748B' }}>Scheduled downgrade</div>
								<div
									style={{ fontWeight: 600, color: isDowngradeScheduled ? '#B45309' : '#475569' }}
								>
									{isDowngradeScheduled
										? `Yes → ${subscription?.downgrade_plan?.name ?? 'target plan'} on ${formatDate(subscription?.downgrade_at)}`
										: 'None'}
								</div>
							</div>
							<div>
								<div style={{ fontSize: '13px', color: '#64748B' }}>Scheduled cancellation</div>
								<div
									style={{
										fontWeight: 600,
										color: subscriptionScheduledForCancellation ? '#B45309' : '#475569',
									}}
								>
									{subscriptionScheduledForCancellation ? 'Yes — ends at cycle end' : 'None'}
								</div>
							</div>
							<div>
								<div style={{ fontSize: '13px', color: '#64748B' }}>Plan change mechanism</div>
								<div style={{ fontWeight: 600 }}>
									{canRepriceInPlace
										? 'In-place update (UPI mandate)'
										: 'Replacement subscription (card mandate needs re-authorisation)'}
								</div>
							</div>
						</div>
					</section>

					{/* ── the two states people confuse ────────────────────────────── */}
					<div
						className="mb-4 p-3 rounded"
						style={{
							backgroundColor: '#FEF3C7',
							border: '1px solid #FCD34D',
							fontSize: FONT.small,
						}}
					>
						<strong>Downgrade and cancellation are different states.</strong> A scheduled{' '}
						<em>downgrade</em> moves you to a cheaper <em>paid</em> plan; a scheduled{' '}
						<em>cancellation</em> ends the subscription and drops you to <em>Free</em>. Running the
						cancellation action while a downgrade is scheduled aborts with &ldquo;no subscription
						with cancel_at_cycle_end found&rdquo; — that is correct behaviour, not a failure.
					</div>

					<div className="row g-4 mb-4">
						<div className="col-12 col-xl-4">
							<TestAction
								title="Run scheduled downgrade now"
								description="Performs the plan cutover that would normally happen when the current paid period ends: stops billing on the old subscription, then switches the organisation onto the cheaper plan."
								whenItWorks="After you have chosen a lower paid plan and completed the Razorpay authorisation for it. Use this instead of waiting for the renewal date."
								requirements={[
									{
										label: 'A downgrade is scheduled on this organisation',
										met: isDowngradeScheduled,
									},
									{ label: 'Currently on a paid plan', met: isPaid },
								]}
								steps={[
									'Open the pricing modal and pick a lower paid plan.',
									'Complete the Razorpay authorisation when prompted.',
									'Confirm "Scheduled downgrade" above shows the target plan.',
									'Then run this action.',
								]}
								outcome="Cancels the old subscription with Razorpay so it cannot renew, marks it superseded, and activates the downgraded plan. The organisation's feature limits change immediately."
								buttonLabel="Run downgrade cutover"
								isLoading={loading.process_downgrade}
								onRun={() =>
									runTest(
										'process_downgrade',
										testDowngradeSubscription,
										'Downgrade cutover completed.',
									)
								}
							/>
						</div>

						<div className="col-12 col-xl-4">
							<TestAction
								title="Run downgrade to Free now"
								description="Ends the current paid subscription and activates the Free plan, as would happen at the end of the paid period."
								whenItWorks="After choosing the Free plan from the pricing modal, or after cancelling a subscription — both leave the organisation due to land on Free."
								requirements={[
									{ label: 'Currently on a paid plan', met: isPaid },
									{
										label: 'A downgrade to Free or a cancellation is scheduled',
										met: isDowngradeScheduled || Boolean(subscriptionScheduledForCancellation),
									},
								]}
								steps={[
									'Choose the Free plan from the pricing modal, or cancel the subscription.',
									'Confirm the panel above shows a scheduled downgrade or cancellation.',
									'Then run this action.',
								]}
								outcome="Ends the paid subscription and creates a Free-plan subscription. Feature limits drop to the Free tier immediately."
								buttonLabel="Run Free downgrade"
								isLoading={loading.downgrade_to_free}
								onRun={() =>
									runTest('downgrade_to_free', testDowngradeToFree, 'Downgrade to Free completed.')
								}
							/>
						</div>

						<div className="col-12 col-xl-4">
							<TestAction
								title="Expire one-time plan now"
								description="Ends a non-recurring (one-time) paid plan as it would at its expiry date, and shows what happens to a downgrade selected on it."
								whenItWorks="Only for a one-time plan — one bought with auto-renewal switched off. This is the ONLY action that can test a non-recurring downgrade: the downgrade cutover runs a move onto a next plan, which a one-time plan doesn't have, and Finalise cancellation needs a cancellation that was never requested."
								requirements={[
									{ label: 'Currently on a paid plan', met: isPaid },
									{ label: 'That plan is one-time (not recurring)', met: !isSubscriptionRecurring },
								]}
								steps={[
									'Subscribe to a paid plan with auto-renewal switched OFF.',
									'Optionally pick a lower plan from the pricing modal to schedule a downgrade.',
									'Then run this action.',
								]}
								outcome="Ends the paid term and moves the organisation to Free. Any lower plan selected earlier is KEPT as the selected next plan rather than applied — that is the defined non-recurring behaviour, not a failure."
								buttonLabel="Expire one-time plan"
								isLoading={loading.expire_non_recurring}
								onRun={() =>
									runTest(
										'expire_non_recurring',
										testExpireNonRecurring,
										'One-time plan expired — organisation moved to Free.',
									)
								}
							/>
						</div>

						<div className="col-12 col-xl-4">
							<TestAction
								title="Finalise cancellation now"
								description="Completes a pending 'cancel at cycle end' request immediately, instead of waiting for the cycle to end."
								whenItWorks="Only after using Cancel Subscription on the My Subscription page. This is NOT the action for a scheduled downgrade."
								requirements={[
									{
										label: 'A cancellation is scheduled (cancel at cycle end)',
										met: Boolean(subscriptionScheduledForCancellation),
									},
									{ label: 'Currently on a paid plan', met: isPaid },
								]}
								steps={[
									'Go to Settings → My Subscription.',
									'Click Cancel Subscription and confirm.',
									'Confirm "Scheduled cancellation" above shows Yes.',
									'Then run this action.',
								]}
								outcome="Cancels the subscription with Razorpay, marks it cancelled locally, and moves the organisation to the Free plan."
								buttonLabel="Finalise cancellation"
								isLoading={loading.finalize_cancellation}
								onRun={() =>
									runTest(
										'finalize_cancellation',
										testFinalizeCancellation,
										'Cancellation finalised.',
									)
								}
							/>
						</div>
					</div>

					{/* ── subscription utilities ───────────────────────────────────── */}
					<div className="row g-4">
						<div className="col-12 col-md-6">
							<section className="border rounded-3 p-4 h-100 bg-white">
								<h4
									className="mb-2"
									style={{ fontSize: '17px', fontWeight: 700 }}
								>
									Sync plans from Razorpay
								</h4>
								<p style={{ fontSize: FONT.small, color: '#475569' }}>
									Pulls the latest plan list from Razorpay into the local database. Run this after
									creating or editing a plan in the Razorpay dashboard, and before testing any plan
									change — otherwise the pricing modal offers plan ids that do not exist locally.
								</p>
								<button
									type="button"
									className="btn btn-outline-primary"
									style={{ fontSize: FONT.base }}
									disabled={loading.sync_plan}
									onClick={handleSyncPlans}
								>
									{loading.sync_plan ? 'Syncing…' : 'Sync subscription plans'}
								</button>
							</section>
						</div>
					</div>
				</>
			)}

			{activeTab === 'notifications' && (
				<div className="row g-4">
					<div className="col-12 col-xl-4">
						<TestAction
							title="Raise a usage limit alert"
							description="Creates a subscription usage alert for this organisation right now, so the dashboard banner and the notification dropdown can be checked without filling up a plan's seats or waiting for the hourly scheduler."
							whenItWorks="Always — it writes the notification directly rather than queueing it, so no dev:worker process is needed. Only 90% and 100% alerts reach the dashboard banner; 50% and 75% go to the notification bell only, which is by design."
							requirements={[]}
							steps={[
								'Run this action.',
								'Reload the dashboard — one banner should appear below the top bar.',
								'Use "3 duplicates" to confirm repeats collapse into a single banner.',
							]}
							outcome="One banner per feature and threshold, however many rows exist. Dismissing it retires every duplicate of that same alert, so it should not come back on reload."
							buttonLabel="Limit reached (100%)"
							tone="primary"
							isLoading={loading.usage_alert}
							onRun={() =>
								runTest(
									'usage_alert',
									() => testUsageAlert({ feature: 'users', threshold: 100, count: 1 }),
									'Usage alert created — reload to see the banner.',
								)
							}
							secondaryLabel="3 duplicates"
							isSecondaryLoading={loading.usage_alert_dupes}
							onRunSecondary={() =>
								runTest(
									'usage_alert_dupes',
									() => testUsageAlert({ feature: 'users', threshold: 100, count: 3 }),
									'3 duplicate alerts created — the banner should still show only ONE.',
								)
							}
						/>
					</div>
				</div>
			)}

			{activeTab === 'diagnostics' && (
				<div className="row g-4">
					<div className="col-12 col-md-6">
						<section className="border rounded-3 p-4 h-100 bg-white">
							<h4
								className="mb-2"
								style={{ fontSize: '17px', fontWeight: 700 }}
							>
								Sentry test error
							</h4>
							<p style={{ fontSize: FONT.small, color: '#475569' }}>
								Throws a deliberate error to confirm Sentry is receiving events. Nothing in the
								subscription flow is affected.
							</p>
							<button
								type="button"
								className="btn btn-outline-danger"
								style={{ fontSize: FONT.base }}
								onClick={() => {
									throw new Error('Sentry Test');
								}}
							>
								Send Sentry test error
							</button>
						</section>
					</div>
				</div>
			)}
		</main>
	);
}

export default Developer;
